import { useTranslation } from 'react-i18next'

import {
  DEFAULT_TABLE_DENSITY,
  TABLE_DENSITIES,
  useSetTableCellDividers,
  useSetTableDensity,
  useSetTableRowDividers,
  useSetTableStripedRows,
  useTableCellDividers,
  useTableDensity,
  useTableRowDividers,
  useTableStripedRows,
} from '#/shared/lib/table-density'
import { cn } from '#/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { CheckIcon, SettingsIcon } from '#/shared/ui/icons'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <DropdownMenuLabel className="px-2 py-1 text-2xs font-semibold tracking-wide text-gray-400 uppercase">
      {children}
    </DropdownMenuLabel>
  )
}

function OptionRow({
  label,
  active,
  onSelect,
}: {
  label: string
  active: boolean
  onSelect: () => void
}) {
  return (
    <DropdownMenuItem
      onSelect={(event) => {
        event.preventDefault()
        onSelect()
      }}
      className={cn(
        'justify-between gap-2 text-sm',
        active ? 'text-gray-900' : 'text-gray-600',
      )}
    >
      {label}
      <CheckIcon
        className={cn('size-3 text-gray-500', !active && 'invisible')}
      />
    </DropdownMenuItem>
  )
}

/**
 * Gear menu pinned to the right edge of a table header. Overlaid (absolute) by
 * the caller so it never shifts header column positions. Exposes the global,
 * persisted row density plus the cell-divider / row-divider / striped-row
 * appearance preferences.
 */
export function TableSettingsMenu() {
  const { t } = useTranslation()
  const density = useTableDensity()
  const setDensity = useSetTableDensity()
  const cellDividers = useTableCellDividers()
  const setCellDividers = useSetTableCellDividers()
  const rowDividers = useTableRowDividers()
  const setRowDividers = useSetTableRowDividers()
  const stripedRows = useTableStripedRows()
  const setStripedRows = useSetTableStripedRows()

  const isDefault =
    density === DEFAULT_TABLE_DENSITY &&
    !cellDividers &&
    rowDividers &&
    !stripedRows

  const resetToDefaults = () => {
    setDensity(DEFAULT_TABLE_DENSITY)
    setCellDividers(false)
    setRowDividers(true)
    setStripedRows(false)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('table.settings.label')}
        className="flex size-7 cursor-pointer items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none"
      >
        <SettingsIcon className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 p-1">
        <SectionLabel>{t('table.density.title')}</SectionLabel>
        {TABLE_DENSITIES.map((value) => (
          <OptionRow
            key={value}
            label={t(`table.density.options.${value}`)}
            active={value === density}
            onSelect={() => setDensity(value)}
          />
        ))}
        <DropdownMenuSeparator />
        <SectionLabel>{t('table.appearance.title')}</SectionLabel>
        <OptionRow
          label={t('table.appearance.cellDividers')}
          active={cellDividers}
          onSelect={() => setCellDividers(!cellDividers)}
        />
        <OptionRow
          label={t('table.appearance.rowDividers')}
          active={rowDividers}
          onSelect={() => setRowDividers(!rowDividers)}
        />
        <OptionRow
          label={t('table.appearance.stripedRows')}
          active={stripedRows}
          onSelect={() => setStripedRows(!stripedRows)}
        />
        <DropdownMenuItem
          disabled={isDefault}
          onSelect={(event) => {
            event.preventDefault()
            resetToDefaults()
          }}
          className="mt-2 cursor-pointer justify-center rounded-md border border-gray-200 bg-gray-50 py-1.5 text-sm font-medium text-gray-700 focus:bg-gray-100 focus:text-gray-900"
        >
          {t('table.settings.reset')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
